<template>
  <tm-dropdown
    ref="tmDropdownElement"
    :no-focus="false"
    :model-value="dropdownModelValue"
    :max-width="maxWidth"
    :width="menuWidth"
    :disable="disable"
    @update:model-value="$emit('update:dropdown', $event)"
    @show="showHandler"
    @before-hide="$emit('before-hide')"
    @hide="hideHandler"
  >
    <slot name="selected-item" />

    <template v-slot:menu="props">
      <slot
        name="before-options"
        v-bind="props"
      />
      <template v-if="preparedOptions.length">
        <tm-dropdown-item
          v-for="(option, index) in preparedOptions"
          :key="option.value"
          :ref="(el) => setOptionElement(el, index)"
          tabindex="-1"
          :focused="isOptionFocused(option, index)"
          @click="$emit('update:model-value', option.value)"
        >
          <slot
            name="option"
            :opt="option"
          >
            <tm-icon
              v-if="option.icon"
              :name="option.icon"
              class="neutral--text"
              size="small"
              left
            />
            <span>{{ option.text }}</span>
          </slot>
        </tm-dropdown-item>
      </template>
      <q-item v-else>
        <q-item-section class="text-grey">{{ $t('common.noResults') }}</q-item-section>
      </q-item>

      <div ref="bottomElement" />
    </template>
  </tm-dropdown>
</template>

<script lang="ts">
import type { ComponentPublicInstance, PropType } from '@/composition/vue/compositionApi'
import { computed, defineComponent, onBeforeUnmount, ref, watch } from '@/composition/vue/compositionApi'
import TmDropdownItem from '@/components/shared/TmDropdownItem.vue'
import TmDropdown from '@/components/shared/TmDropdown.vue'
import TmIcon from '@/components/shared/TmIcon.vue'
import type { SelectOption } from '@/services/forms/types'
import { useDropdownOptionsNavigation } from '@/composition/dropdown'

export default defineComponent({
  components: {
    TmDropdown,
    TmDropdownItem,
    TmIcon,
  },
  props: {
    options: {
      type: Array as PropType<SelectOption[]>,
      required: true,
    },
    highlightActive: Boolean,
    activeOption: String,
    dropdownModelValue: Boolean,
    maxWidth: String,
    menuWidth: String,
    disable: {
      type: Boolean,
    },
  },
  emits: {
    'update:model-value': (_val: string) => true,
    'update:dropdown': (_val: boolean) => true,
    show: () => true,
    hide: (_e?: Event) => true,
    'before-hide': () => true,
  },
  setup(props, context) {
    const tmDropdownElement = ref<InstanceType<typeof TmDropdown>>()
    const bottomElement = ref<HTMLElement>()
    const optionElements = ref<(HTMLElement | undefined)[]>([])
    const isOpen = ref(false)

    const loadMoreLookahead = 5
    const gap = 20

    const quantityShowedElements = ref(gap)

    const preparedOptions = computed(() => {
      if (quantityShowedElements.value >= props.options.length) {
        return props.options
      }

      return props.options.slice(0, quantityShowedElements.value)
    })

    const intersectHandler = () => {
      quantityShowedElements.value += gap
    }

    let observer: IntersectionObserver | null = null

    onBeforeUnmount(() => {
      observer?.disconnect()
    })

    const setOptionElement = (el: Element | ComponentPublicInstance | null, index: number) => {
      optionElements.value[index] = (el as ComponentPublicInstance | null)?.$el
    }

    const { focusedIndex, resetFocusedOption } = useDropdownOptionsNavigation({
      isOpen,
      optionsCount: computed(() => preparedOptions.value.length),
      getOptionElement: (index: number) => optionElements.value[index],
      close: () => tmDropdownElement.value?.close(),
    })

    const isOptionFocused = (option: SelectOption, index: number) => {
      if (focusedIndex.value !== -1) {
        return focusedIndex.value === index
      }
      return props.highlightActive && option.value === props.activeOption
    }

    watch(focusedIndex, (index) => {
      if (index >= 0 && index >= preparedOptions.value.length - loadMoreLookahead) {
        intersectHandler()
      }
    })

    const showHandler = () => {
      isOpen.value = true
      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0]
          if (!entry.isIntersecting) {
            return
          }
          intersectHandler()
        },
        {
          root: tmDropdownElement.value!.getMenuWrapperElement(),
          rootMargin: '0px 0px 100px 0px',
        },
      )
      observer.observe(bottomElement.value!)
      context.emit('show')
    }

    const hideHandler = (e?: Event) => {
      isOpen.value = false
      context.emit('hide', e)
      observer?.disconnect()
      quantityShowedElements.value = gap
    }

    watch(
      () => props.options.length,
      () => {
        quantityShowedElements.value = gap
        optionElements.value = []
        resetFocusedOption()
      },
    )

    return {
      tmDropdownElement,
      bottomElement,
      preparedOptions,
      setOptionElement,
      isOptionFocused,
      showHandler,
      hideHandler,
    }
  },
})
</script>
